<script setup lang="ts">
import { computed, ref } from "vue";
import MLink from "../MLink";
import MModal from "../MModal";
import MLayoutStack from "../MLayoutStack";
import MButton from "../MButton";
import axios from "../../../utils/axios";
import { listen } from "../../../utils/bus";
import { useTelephony } from "../../../store";

//// PROPS
const props = defineProps({
  number: {
    type: null,
    default: "",
  },
});

//// LOCAL VARS
const toggleOutboundPhoneCall = ref<Boolean>(false);
const userNotFound = ref<Boolean>(false);
const callFailureResponse = ref("");

//// COMPUTED VARS
const hasTelephonyProvider = computed(() => {
  const { telephonyId } = useTelephony();
  return telephonyId != null;
});

const providerName = computed(() => {
  const { providerName } = useTelephony();
  return providerName;
});

const loggedInStaffIsConfigured = computed(() => {
  const { loggedInStaffIsConfigured } = useTelephony();
  return loggedInStaffIsConfigured;
});

listen("modal:close", close);

//// FUNCTIONS
function callTelephoneNumber() {
  axios
    .post(
      `/communication/telephony/call-telephone-number`,
      { telephoneNumber: props.number },
      { showError: false },
    )
    .then(() => {
      toggleOutboundPhoneCall.value = false;
    })
    .catch((e) => {
      const failureMessage = e.response.data.errors.command[0];
      if (failureMessage === "Staff is not configured with telephony provider") {
        callFailureResponse.value = "";
        userNotFound.value = true;
      } else {
        callFailureResponse.value = failureMessage;
        userNotFound.value = false;
      }
    });
}

function open() {
  if (loggedInStaffIsConfigured.value) {
    toggleOutboundPhoneCall.value = true;
    userNotFound.value = false;
  } else {
    toggleOutboundPhoneCall.value = false;
    userNotFound.value = true;
  }
}

function close() {
  toggleOutboundPhoneCall.value = false;
  callFailureResponse.value = "";
  userNotFound.value = false;
}
</script>

<template>
  <span v-if="hasTelephonyProvider">
    <m-link :on-click="open">{{ number }}</m-link>
  </span>
  <span v-else>{{ number }}</span>

  <m-modal v-model="toggleOutboundPhoneCall" title="Outbound Phone Call" type="dialog">
    <m-layout-stack gap="4">
      <p>
        Would you like to call <strong>{{ number }}</strong
        >?
      </p>
      <p v-if="callFailureResponse" class="error-text">{{ callFailureResponse }}</p>
      <m-layout-stack horizontal end>
        <m-button label="No, cancel" color="secondary" :on-click="close" />
        <m-button label="Yes, dial" :on-click="callTelephoneNumber" />
      </m-layout-stack>
    </m-layout-stack>
  </m-modal>

  <m-modal v-model="userNotFound" title="Unable to Find User" type="dialog">
    <m-layout-stack gap="4">
      <p>
        Medicus wasn't able to find your user account in {{ providerName }}.
        <br />
        Please contact your local Medicus administrator if you think this is incorrect
      </p>
      <m-layout-stack horizontal end>
        <m-button label="Acknowledge" color="secondary" :on-click="close" />
      </m-layout-stack>
    </m-layout-stack>
  </m-modal>
</template>

<style scoped lang="scss">
.error-text {
  color: var(--status-red);
}
</style>
